/**
 * 로그인 진입 메인화면 — 세로 2분할.
 * 왼쪽: 기능개선관리대장 전체 요약(마스터 전용). 오른쪽: 후속 위젯 자리.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import { useTab } from 'contexts/TabContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, Card, CardTitle, ActionButton } from 'styles/common.styles';
import { theme, cssVar } from 'styles/theme';
import { loadFeatureList } from 'master/featureImprovementStore';

const FEATURE_LEDGER_TAB = {
  id: '270',
  label: '기능개선관리대장',
  path: '/master/feature-improvement',
  component: 'ADM_FEATURE_LEDGER',
  componentPath: 'pages/master/FeatureImprovementLedger',
  menuPath: ['기준정보', '기능개선관리대장'],
};

const COL_PREFS_PREFIX = 'erp.home.featureWidget.cols';

const COLS = [
  { id: 'no', label: 'No.', min: 40, def: 48, className: 'num' },
  { id: 'item', label: '항목', min: 72, def: 110, className: 'item' },
  { id: 'content', label: '내용', min: 140, def: 280, className: 'content' },
  { id: 'date', label: '배포일자', min: 80, def: 96, className: 'date' },
];

const DEFAULT_WIDTHS = COLS.reduce((acc, c) => {
  acc[c.id] = c.def;
  return acc;
}, {});

function prefsKey(prefsUserId) {
  return `${COL_PREFS_PREFIX}|${prefsUserId || 'anon'}`;
}

function readWidths(prefsUserId) {
  try {
    const raw = localStorage.getItem(prefsKey(prefsUserId));
    if (!raw) return { ...DEFAULT_WIDTHS };
    const parsed = JSON.parse(raw);
    const next = { ...DEFAULT_WIDTHS };
    COLS.forEach((c) => {
      const n = Number(parsed?.[c.id]);
      if (Number.isFinite(n)) next[c.id] = Math.max(c.min, Math.round(n));
    });
    return next;
  } catch {
    return { ...DEFAULT_WIDTHS };
  }
}

function writeWidths(prefsUserId, widths) {
  try {
    localStorage.setItem(prefsKey(prefsUserId), JSON.stringify(widths));
  } catch {
    /* ignore */
  }
}

const Split = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$twoCol ? '1fr 1fr' : '1fr')};
  gap: ${theme.spacing.md};
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: ${theme.spacing.md};
`;

const WidgetTitle = styled(CardTitle)`
  margin: 0 0 ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.md};
  flex-shrink: 0;
`;

const TableWrap = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const SummaryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.fontSize.sm};
  table-layout: fixed;

  th,
  td {
    padding: 6px 8px;
    border-bottom: 1px solid ${cssVar('color-border', theme.colors.border)};
    text-align: left;
    vertical-align: top;
  }

  th {
    color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
    font-weight: 600;
    position: sticky;
    top: 0;
    background: ${cssVar('color-bg-white', theme.colors.white)};
    white-space: nowrap;
    user-select: none;
  }

  th.num,
  td.num,
  th.date,
  td.date {
    text-align: center;
    white-space: nowrap;
  }

  td.item {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  td.content {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.45;
    overflow: visible;
  }
`;

const ThInner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 18px;
`;

const ColResize = styled.span`
  position: absolute;
  top: -6px;
  right: -8px;
  width: 8px;
  height: calc(100% + 12px);
  cursor: col-resize;
  z-index: 2;

  &:hover,
  &:active {
    background: ${cssVar('color-border', theme.colors.border)};
  }
`;

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding-top: ${theme.spacing.sm};
`;

const EmptyHint = styled.div`
  padding: ${theme.spacing.md} 4px;
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  font-size: ${theme.typography.fontSize.sm};
`;

const PlaceholderBody = styled.p`
  margin: 0;
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.6;
`;

/** 대장 화면과 동일: seq_no 오름차순 전체. */
function summaryFeatures(rows = []) {
  return [...(rows || [])].sort((a, b) => (Number(a.seqNo) || 0) - (Number(b.seqNo) || 0));
}

const FeatureSummaryWidget = ({ onOpenLedger, prefsUserId }) => {
  const { activeTabId } = useTab();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [widths, setWidths] = useState(() => readWidths(prefsUserId));
  const dragRef = useRef(null);

  useEffect(() => {
    setWidths(readWidths(prefsUserId));
  }, [prefsUserId]);

  useEffect(() => {
    if (activeTabId && activeTabId !== 'HOME_MAIN') return undefined;
    let cancelled = false;
    setLoading(true);
    loadFeatureList({})
      .then((list) => {
        if (!cancelled) setRows(summaryFeatures(list));
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTabId]);

  const onResizeStart = useCallback(
    (e, colId) => {
      e.preventDefault();
      e.stopPropagation();
      const col = COLS.find((c) => c.id === colId);
      dragRef.current = {
        id: colId,
        startX: e.clientX,
        startW: widths[colId],
        min: col?.min || 40,
      };
    },
    [widths]
  );

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const nextW = Math.max(drag.min, Math.round(drag.startW + (e.clientX - drag.startX)));
      setWidths((prev) => ({ ...prev, [drag.id]: nextW }));
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setWidths((prev) => {
        writeWidths(prefsUserId, prev);
        return prev;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [prefsUserId]);

  const tableWidth = useMemo(
    () => COLS.reduce((sum, c) => sum + (Number(widths[c.id]) || c.def), 0),
    [widths]
  );

  return (
    <Panel>
      <WidgetTitle>기능개선관리대장</WidgetTitle>
      <TableWrap>
        <SummaryTable style={{ width: '100%', minWidth: tableWidth }}>
          <colgroup>
            {COLS.map((c) => (
              <col key={c.id} style={{ width: widths[c.id] }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {COLS.map((c) => (
                <th key={c.id} className={c.className} title="열 경계 드래그로 너비 조절">
                  <ThInner>
                    {c.label}
                    <ColResize
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`${c.label} 열 너비 조절`}
                      onMouseDown={(e) => onResizeStart(e, c.id)}
                    />
                  </ThInner>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.featureUid || `${r.seqNo}-${r.itemNm}`}>
                <td className="num">{idx + 1}</td>
                <td className="item" title={r.itemNm || ''}>
                  {r.itemNm || ''}
                </td>
                <td className="content">{r.content || ''}</td>
                <td className="date">{r.deployDate || '—'}</td>
              </tr>
            ))}
          </tbody>
        </SummaryTable>
        {!loading && rows.length === 0 && <EmptyHint>등록된 항목이 없습니다.</EmptyHint>}
      </TableWrap>
      <Footer>
        <ActionButton type="button" onClick={onOpenLedger}>
          전체 보기 →
        </ActionButton>
      </Footer>
    </Panel>
  );
};

const MainHome = () => {
  const { user, isMaster } = useAuth();
  const { openTab } = useTab();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const openLedger = useCallback(() => {
    openTab({ ...FEATURE_LEDGER_TAB });
  }, [openTab]);

  return (
    <PageContainer>
      <Split $twoCol={!!isMaster}>
        {isMaster && <FeatureSummaryWidget onOpenLedger={openLedger} prefsUserId={prefsUserId} />}
        <Panel>
          <WidgetTitle>추가 위젯 영역</WidgetTitle>
          <PlaceholderBody>추가 위젯 영역 — 준비 중</PlaceholderBody>
        </Panel>
      </Split>
    </PageContainer>
  );
};

export default MainHome;
